import { useState, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Pause, Star, MoreVertical, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import narratorService from "@/services/narratorService";
import authService from "@/services/authService";

export default function DemoCard({
  demo,
  isFeatured,
  onPlay,
  onSetFeatured,
  onSubmit,
  onEdit,
  onDelete,
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const audioRef = useRef(null);

  const statusConfig = {
    DRAFT: { label: "Draft", className: "bg-slate-100 text-slate-800" },
    SUBMITTED: { label: "Submitted", className: "bg-[#fff2d8] text-[#9a6500]" },
    APPROVED: { label: "Approved", className: "bg-emerald-50 text-emerald-700" },
    REJECTED: { label: "Rejected", className: "bg-red-100 text-red-800" },
  };

  const config = statusConfig[demo.status] || statusConfig.DRAFT;

  const handlePlayPause = async () => {
    if (!audioRef.current) {
      // Create audio element on first play
      setAudioLoading(true);
      const audio = new Audio();
      
      // Get stream URL with auth token
      const streamUrl = narratorService.getDemoStreamUrl(demo.id);
      const token = authService.getAccessToken();
      
      // Fetch audio with auth header
      try {
        const response = await fetch(streamUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (!response.ok) {
          throw new Error("Failed to load audio");
        }
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        audio.src = blobUrl;
        
        audio.onended = () => {
          setIsPlaying(false);
        };
        
        audio.onerror = () => {
          setAudioLoading(false);
          setIsPlaying(false);
        };
        
        audioRef.current = audio;
        audio.play();
        setIsPlaying(true);
        setAudioLoading(false);
      } catch (error) {
        console.error("Audio load error:", error);
        setAudioLoading(false);
        // Fallback to original play handler
        if (onPlay) onPlay(demo);
      }
    } else {
      // Toggle play/pause
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  return (
    <Card
      data-testid={`demo-card-${demo.id}`}
      className={`border bg-white/95 transition-all hover:shadow-[0_18px_45px_rgba(94,64,18,0.12)] dark:bg-[#11100d]/95 dark:hover:shadow-[0_18px_45px_rgba(0,0,0,0.48)] ${
        isFeatured ? "border-[#c98905] ring-2 ring-[#f5cf75]/45" : "border-[#eadfcd] hover:border-[#d8bd7a] dark:border-[#4d3d16] dark:hover:border-[#8a6a24]"
      }`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-black dark:text-[#f8efe0] truncate">{demo.title}</h4>
              {isFeatured && (
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              <Badge variant="outline" className="text-xs bg-white text-black border-slate-200 dark:border-[#4d3d16] dark:bg-[#15120b] dark:text-[#d5c8ae]">
                {demo.language}
              </Badge>
              {demo.genre_tag && (
                <Badge variant="outline" className="text-xs bg-white text-black border-slate-200 dark:border-[#4d3d16] dark:bg-[#15120b] dark:text-[#d5c8ae]">
                  {demo.genre_tag}
                </Badge>
              )}
              {demo.tone_tag && (
                <Badge variant="outline" className="text-xs bg-white text-black border-slate-200 dark:border-[#4d3d16] dark:bg-[#15120b] dark:text-[#d5c8ae]">
                  {demo.tone_tag}
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Badge className={config.className}>{config.label}</Badge>
              {demo.duration_seconds && (
                <span className="text-xs text-slate-500">
                  {Math.floor(demo.duration_seconds / 60)}:{String(demo.duration_seconds % 60).padStart(2, "0")}
                </span>
              )}
              {demo.size_bytes && (
                <span className="text-xs text-slate-400">
                  {(demo.size_bytes / (1024 * 1024)).toFixed(1)} MB
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handlePlayPause}
              disabled={audioLoading}
              data-testid={`play-demo-${demo.id}`}
              className={isPlaying ? "border-[#d8bd7a] bg-[#fff2d8] dark:bg-[#251b08]" : "border-[#eadfcd] bg-white hover:bg-[#fff8eb] dark:border-[#4d3d16] dark:bg-[#11100d] dark:hover:bg-[#1b1710]"}
            >
              {audioLoading ? (
                <span className="w-4 h-4 border-2 border-[#c98905] border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-4 h-4 text-[#b77900]" />
              ) : (
                <Play className="w-4 h-4 text-[#b77900]" />
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" data-testid={`demo-menu-${demo.id}`}>
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(demo)}>
                  Edit Details
                </DropdownMenuItem>
                {!isFeatured && demo.status === "APPROVED" && (
                  <DropdownMenuItem onClick={() => onSetFeatured(demo.id)}>
                    Set as Featured
                  </DropdownMenuItem>
                )}
                {demo.status === "DRAFT" && (
                  <DropdownMenuItem onClick={() => onSubmit(demo.id)}>
                    Submit for Review
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => onDelete(demo)}
                  className="text-red-600 focus:text-red-700"
                  data-testid={`delete-demo-${demo.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
